import { useMemo } from 'react';
import { GRADIENTS } from '../config/gradients';

/**
 * Per-outcome configuration: enable/disable, goal, map color, rationale, theme.
 * Also manages dashboard-wide year range and theme labels.
 * Pure content — no overlay, no footer. All state lives in parent (DashboardSettings).
 */
export default function CustomizeTab({
  allIndicators,
  onReorder,
  selected, setSelected,
  goalInputs, setGoalInputs,
  gradientSelections, setGradientSelections,
  rationaleInputs, setRationaleInputs,
  yearRange, setYearRange,
  allYears,
  themes, setThemes
}) {
  const sortedYears = useMemo(() => [...allYears].sort((a, b) => a - b), [allYears]);

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }

  function handleGoalChange(id, raw) {
    setGoalInputs(prev => {
      const next = { ...prev };
      if (raw === '' || raw === null) {
        delete next[id];
      } else {
        const num = parseFloat(raw);
        if (!isNaN(num)) next[id] = num;
      }
      return next;
    });
  }

  function selectGradient(indicatorId, gradientId) {
    setGradientSelections(prev => ({ ...prev, [indicatorId]: gradientId }));
  }

  function addTheme() {
    setThemes(prev => [...prev, { id: `theme_${Date.now()}`, label: '', indicatorIds: [] }]);
  }

  function deleteTheme(idx) {
    setThemes(prev => prev.filter((_, i) => i !== idx));
  }

  function updateThemeLabel(idx, label) {
    setThemes(prev => prev.map((t, i) => i === idx ? { ...t, label } : t));
  }

  function setIndicatorTheme(indicatorId, themeId) {
    setThemes(prev => prev.map(t => {
      if (t.id === themeId) {
        return t.indicatorIds.includes(indicatorId) ? t : { ...t, indicatorIds: [...t.indicatorIds, indicatorId] };
      }
      return { ...t, indicatorIds: t.indicatorIds.filter(id => id !== indicatorId) };
    }));
  }

  function clearIndicatorTheme(indicatorId) {
    setThemes(prev => prev.map(t => ({ ...t, indicatorIds: t.indicatorIds.filter(id => id !== indicatorId) })));
  }

  function getIndicatorTheme(indicatorId) {
    return themes.find(t => t.indicatorIds.includes(indicatorId))?.id ?? '';
  }

  function moveUp(id) {
    const idx = allIndicators.findIndex(i => i.id === id);
    if (idx <= 0) return;
    const next = [...allIndicators];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    onReorder(next);
  }

  function moveDown(id) {
    const idx = allIndicators.findIndex(i => i.id === id);
    if (idx < 0 || idx >= allIndicators.length - 1) return;
    const next = [...allIndicators];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    onReorder(next);
  }

  return (
    <div className="customize-tab">

      {/* ── Dashboard-wide settings ── */}
      <div className="admin-global-section">

        <h3 className="admin-global-heading">Year Range</h3>
        <p>Limit which years appear in trend charts and the year selector. Leave both at "All" to show all available data.</p>
        <div className="admin-year-range">
          <div className="admin-year-range-row">
            <label className="admin-year-range-label" htmlFor="yr-from">From</label>
            <select
              id="yr-from"
              className="admin-year-range-select"
              value={yearRange.from ?? ''}
              onChange={e => setYearRange(prev => ({ ...prev, from: e.target.value ? Number(e.target.value) : null }))}
            >
              <option value="">All</option>
              {sortedYears.map(y => (
                <option key={y} value={y} disabled={yearRange.to !== null && y > yearRange.to}>{y}</option>
              ))}
            </select>
          </div>
          <div className="admin-year-range-row">
            <label className="admin-year-range-label" htmlFor="yr-to">To</label>
            <select
              id="yr-to"
              className="admin-year-range-select"
              value={yearRange.to ?? ''}
              onChange={e => setYearRange(prev => ({ ...prev, to: e.target.value ? Number(e.target.value) : null }))}
            >
              <option value="">All</option>
              {sortedYears.map(y => (
                <option key={y} value={y} disabled={yearRange.from !== null && y < yearRange.from}>{y}</option>
              ))}
            </select>
          </div>
          {(yearRange.from !== null || yearRange.to !== null) && (
            <button
              className="admin-year-range-reset"
              onClick={() => setYearRange({ from: null, to: null })}
            >
              Reset to all years
            </button>
          )}
        </div>

        <h3 className="admin-global-heading">Themes</h3>
        <p>Define theme labels to group outcomes under shared headings in the scorecard and tabs. Assign each outcome to a theme below.</p>
        <div className="admin-themes-manage">
          {themes.map((theme, idx) => (
            <div key={theme.id} className="admin-theme-manage-row">
              <input
                className="admin-theme-name-input"
                type="text"
                value={theme.label}
                onChange={e => updateThemeLabel(idx, e.target.value)}
                placeholder="Theme name…"
              />
              <button
                className="admin-theme-delete"
                onClick={() => deleteTheme(idx)}
                aria-label="Delete theme"
              >
                ✕
              </button>
            </div>
          ))}
          <button className="admin-add-theme" onClick={addTheme}>+ Add theme</button>
        </div>

      </div>

      {/* ── Per-outcome settings ── */}
      <h3 className="admin-outcomes-heading">Outcomes</h3>
      <div className="admin-outcomes-list">
        {allIndicators.map((ind, idx) => {
          const activeGradientId = gradientSelections[ind.id] ?? 'green';
          const currentThemeId = getIndicatorTheme(ind.id);
          return (
            <div key={ind.id} className={`admin-outcome-card${selected.has(ind.id) ? '' : ' admin-outcome-card--inactive'}`}>
              <div className="admin-outcome-header">
                <label className="admin-outcome-toggle">
                  <input
                    type="checkbox"
                    checked={selected.has(ind.id)}
                    onChange={() => toggle(ind.id)}
                  />
                  <span className="admin-outcome-name">{ind.label}</span>
                  <span className="admin-indicator-badge">{ind.unit}</span>
                </label>
                <div className="admin-reorder-btns">
                  <button
                    className="admin-reorder-btn"
                    onClick={() => moveUp(ind.id)}
                    disabled={idx === 0}
                    aria-label="Move up"
                  >▲</button>
                  <button
                    className="admin-reorder-btn"
                    onClick={() => moveDown(ind.id)}
                    disabled={idx === allIndicators.length - 1}
                    aria-label="Move down"
                  >▼</button>
                </div>
              </div>
              <div className="admin-outcome-fields">
                <div className="admin-field-row">
                  <label className="admin-field-label">Goal</label>
                  <input
                    type="number"
                    className="admin-goal-input admin-goal-input--inline"
                    placeholder="—"
                    value={goalInputs[ind.id] ?? ''}
                    onChange={e => handleGoalChange(ind.id, e.target.value)}
                    step={ind.unit === 'percent' ? 0.1 : 1}
                    min={0}
                  />
                </div>
                {themes.length > 0 && (
                  <div className="admin-field-row">
                    <label className="admin-field-label">Theme</label>
                    <select
                      className="admin-theme-select"
                      value={currentThemeId}
                      onChange={e => {
                        if (e.target.value === '') {
                          clearIndicatorTheme(ind.id);
                        } else {
                          setIndicatorTheme(ind.id, e.target.value);
                        }
                      }}
                    >
                      <option value="">None</option>
                      {themes.map(t => (
                        <option key={t.id} value={t.id}>{t.label || 'Unnamed theme'}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="admin-field-row admin-field-row--full">
                  <label className="admin-field-label">Map color</label>
                  <div className="admin-gradient-swatches">
                    {GRADIENTS.map(g => (
                      <button
                        key={g.id}
                        className={`admin-gradient-swatch${activeGradientId === g.id ? ' admin-gradient-swatch--active' : ''}`}
                        style={{ background: `linear-gradient(to right, ${g.light}, ${g.dark})` }}
                        title={g.label}
                        onClick={() => selectGradient(ind.id, g.id)}
                      />
                    ))}
                  </div>
                </div>
                <div className="admin-field-row admin-field-row--full">
                  <label className="admin-field-label">Rationale</label>
                  <textarea
                    className="admin-rationale-input"
                    placeholder={ind.rationale ?? 'Enter rationale…'}
                    value={rationaleInputs[ind.id] ?? ''}
                    onChange={e => setRationaleInputs(prev => {
                      const next = { ...prev };
                      if (e.target.value === '') { delete next[ind.id]; } else { next[ind.id] = e.target.value; }
                      return next;
                    })}
                    rows={2}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
